import { AssistantBill, AssistantBillState, AssistantBillType } from '@mews-commander/data-access';
import { TextKey, RouteName } from '@mews-commander/enums';
import { lt, useLocalizeText } from '@mews/framework';
import { createHandler } from '@mews/std';
import { GridItem, IconName, Stack, Tag, TagVariant } from '@optimus-web/core';
import { useResolveRoute } from '@mews/routing';
import { getFullName } from '@mews-commander/utils';
import { navigateInBaseModal } from '../../../app/actions/common';
import { ResultListProps } from '../results/ListTitle';
import { stringifyPrimaryText } from '../../utils/array';
import { highlight } from '../../utils/highlight';
import { StyledBadge, StyledLeftIcon } from '../styles';
import { SearchType } from '../../assistantSearchTypes';
import { DescriptionItem, SearchItemDescription } from '../components/SearchItemDescription';
import { useLocalizedDate } from './useLocalizedDate';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';

interface BillListProps extends ResultListProps {
    bills: AssistantBill[];
}

const billTypeTextKey = {
    [AssistantBillType.Invoice]: TextKey.Invoice,
    [AssistantBillType.Receipt]: TextKey.Receipt,
    [AssistantBillType.None]: TextKey.None,
};

interface BillListItemProps extends ResultListProps {
    bill: AssistantBill;
}

const PrimaryText = ({ bill, searchTerm }: BillListItemProps) => {
    const number = bill.number ?? '';
    const name = bill.name ?? '';
    const numberAndName = highlight(stringifyPrimaryText([number, name]), searchTerm);
    const billTypeKey = billTypeTextKey[bill.type];
    const billType = useLocalizeText()({ textKey: billTypeKey });
    return (
        <Stack vertical={false} spacing="50">
            <span>{numberAndName}</span>
            {bill.type === AssistantBillType.Invoice && <StyledBadge content={billType} variant="subtle" />}
        </Stack>
    );
};

const SecondaryText = ({ bill, searchTerm }: BillListItemProps) => {
    const { customer, company, dueUtc } = bill;
    const formattedDueDate = useLocalizedDate(dueUtc);
    const customerName = getFullName(customer);
    const companyName = company ? company.name : '';

    const descriptions: DescriptionItem[] = [
        ...(customerName ? [{ value: highlight(customerName, searchTerm) }] : []),
        ...(companyName ? [{ value: highlight(companyName, searchTerm) }] : []),
        ...(dueUtc ? [{ value: formattedDueDate }] : []),
    ];

    return <SearchItemDescription descriptions={descriptions} />;
};

interface RightItemProps {
    bill: AssistantBill;
}

const getTextKey = (bill: AssistantBill): Mews.TextKey => {
    const today = new Date();
    const dueDate = bill.dueUtc ? new Date(bill.dueUtc) : null;
    const paidDate = bill.paidUtc ? new Date(bill.paidUtc) : null;

    if (bill.type === AssistantBillType.Receipt) {
        return TextKey.Closed;
    }
    if (paidDate && paidDate < today) {
        return TextKey.Paid;
    }

    if (dueDate && dueDate < today) {
        return TextKey.Overdue;
    }

    return TextKey.Unpaid;
};

const getTagVariant = createHandler<Mews.TextKeyUnion, TagVariant>(
    {
        [TextKey.Paid]: 'success',
        [TextKey.Overdue]: 'warning',
        [TextKey.Unpaid]: 'primary',
        [TextKey.Closed]: 'basicBold',
    },
    'basicBold'
);

const RightItem = ({ bill }: RightItemProps) => {
    const textKey = getTextKey(bill);
    const variant = getTagVariant(textKey);

    return (
        <GridItem alignSelf="end">
            <Tag variant={variant}>{lt(textKey)}</Tag>
        </GridItem>
    );
};

interface BillListItemProps extends ResultListProps {
    bill: AssistantBill;
}

export const useBillList = ({ bills, searchTerm }: BillListProps): AssistantSearchListItemProps[] => {
    const resolveRoute = useResolveRoute();

    return bills.map((bill) => {
        const url = resolveRoute(RouteName.BillDetail, { id: bill.id });
        const showRightItem: boolean =
            (bill.type === AssistantBillType.Receipt && bill.state === AssistantBillState.Closed) ||
            (bill.type === AssistantBillType.Invoice && bill.state !== AssistantBillState.None);

        return {
            itemId: bill.id,
            primaryText: <PrimaryText bill={bill} searchTerm={searchTerm} />,
            secondaryText: <SecondaryText bill={bill} searchTerm={searchTerm} />,
            leftIcon: <StyledLeftIcon size="small" value={IconName.BillsInvoices} />,
            routeAction: navigateInBaseModal(url),
            rightItem: showRightItem ? <RightItem bill={bill} /> : undefined,
            searchType: SearchType.Bill,
        };
    });
};
